import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useApi, type ConversationListItem } from '@/shared/api';
import { useObjectScope } from '@/shared/object-scope';
import { demoInbox } from './demo/demoData';
import { awaitingReply, type InboxFilter } from './inboxModel';
import { useConversationMessages } from './useConversationMessages';
import { useThreadMarkRead } from './useThreadMarkRead';

const PAGE_SIZE = 50;

function matchesFilter(
  t: ConversationListItem,
  filter: InboxFilter,
  awaitingOnly: boolean,
): boolean {
  if (awaitingOnly && !awaitingReply(t)) return false;
  if (filter === 'Alle') return true;
  if (filter === 'Sonstiges') return t.channel !== 'Airbnb' && t.channel !== 'BookingCom';
  return t.channel === filter;
}

/**
 * Daten und Auswahl des Postfachs an einer Stelle — die Komposition (drei Spalten, zwei Spalten,
 * eine Ebene am Handy) liest denselben Zustand, die Auswahl hängt nie an der sichtbaren Fassung.
 * Liste und Zähler kommen vom Server und teilen sich den Objektfilter; der Zähler „Nicht
 * beantwortet“ gilt für alle Kanäle, auch wenn die Liste auf einen gefiltert ist.
 */
export function useInboxThreads() {
  const api = useApi();
  const { propertyIds } = useObjectScope();
  // Dev-Vorschau aus der URL: ?demo=1 → Beispieldaten; ?thread=<id> → Vorauswahl.
  const urlParams = new URLSearchParams(window.location.search);
  const demo = urlParams.get('demo') === '1' || urlParams.get('demo') === 'true';

  const [selectedId, setSelectedId] = useState<string | null>(
    urlParams.get('thread') ?? (demo ? 'demo-1' : null),
  );
  const [filter, setFilter] = useState<InboxFilter>('Alle');
  // Nicht beantwortet ist der Arbeitsvorrat und deshalb die Startansicht.
  const [awaitingOnly, setAwaitingOnly] = useState(true);

  const list = useInfiniteQuery({
    queryKey: ['inbox', 'threads', filter, awaitingOnly, propertyIds],
    queryFn: ({ pageParam }) =>
      api.messaging.list({
        channel: filter === 'Alle' ? undefined : filter,
        awaitingReplyOnly: awaitingOnly,
        cursor: pageParam ?? undefined,
        take: PAGE_SIZE,
        propertyIds,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled: !demo,
  });
  const summary = useQuery({
    queryKey: ['inbox', 'unread', 'scope', propertyIds],
    queryFn: () => api.messaging.unread(propertyIds),
    enabled: !demo,
    refetchInterval: 30_000,
  });

  const threads = demo
    ? demoInbox.threads.filter((t) => matchesFilter(t, filter, awaitingOnly))
    : (list.data?.pages.flatMap((page) => page.items) ?? []);
  // Die Auswahl bleibt, bis das Objekt wechselt. Eine beantwortete Unterhaltung fällt aus
  // „Nicht beantwortet“ heraus, darf aber nicht mitten im Lesen verschwinden; ob man sie sehen
  // darf, entscheidet der Server beim Abruf.
  const scopeKey = propertyIds?.join(',') ?? 'alle';
  const lastScope = useRef(scopeKey);
  useEffect(() => {
    if (lastScope.current === scopeKey) return;
    lastScope.current = scopeKey;
    setSelectedId(null);
  }, [scopeKey]);

  const detail = useQuery({
    queryKey: ['inbox', 'thread', selectedId],
    queryFn: () => api.messaging.get(selectedId!),
    enabled: !demo && !!selectedId,
  });
  const messages = useConversationMessages(selectedId, !demo);

  const selectedThread = demo ? demoInbox.detail(selectedId) : detail.data;
  const markRead = useThreadMarkRead(selectedId, selectedThread, !demo);

  return {
    demo,
    threads,
    list,
    listLoading: !demo && list.isLoading,
    // Offline pausiert TanStack still: weder lädt es, noch ist es leer.
    listPaused: !demo && list.isPending && list.fetchStatus === 'paused',
    detailPaused: !demo && detail.isPending && detail.fetchStatus === 'paused',
    awaitingCount: demo ? threads.filter(awaitingReply).length : summary.data?.awaitingReply,
    refresh: () => {
      void list.refetch();
      void summary.refetch();
    },
    selectedId,
    setSelectedId,
    filter,
    setFilter,
    awaitingOnly,
    setAwaitingOnly,
    thread: selectedThread,
    // Ein gewähltes Gespräch, dessen Detail noch lädt, ist am Handy eine eigene Ebene und braucht
    // eine ehrliche Zwischenanzeige — die Query-Zustände, nicht „gewählt, aber nichts da“, das
    // auch nach einem gescheiterten Abruf für immer auf „lädt“ stünde.
    detailLoading: detail.isLoading,
    detailError: detail.error,
    retryDetail: () => void detail.refetch(),
    messages: demo ? demoInbox.messages(selectedId) : messages.items,
    messagesState: messages,
    demoCtx: demo ? demoInbox.context(selectedId) : undefined,
    markRead,
  };
}
